import React, { useEffect, useState } from 'react';
import Select, { components } from 'react-select';
import { AsyncMultiSelectFieldProps } from './types';
import { MultiValue, MenuListProps } from 'react-select';
import { SelectOption } from '../../../shared/utils/asyncSelectApi';
import { useAsyncSelect } from '../../../shared/hooks/useAsyncSelect';

/**
 * Async multi-select field component with API-based pagination and search
 * Supports multiple selection with debounced search and infinite scrolling
 */
const AsyncMultiSelectField: React.FC<AsyncMultiSelectFieldProps> = ({
  id,
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  onFocus,
  onBlur,
  hasValue,
  getFloatingLabelStyle,
  getFloatingInputStyle,
  styles,
  apiEndpoint,
  searchField,
  valueField = 'id',
  labelField = 'name',
  pageSize = 20,
}) => {
  // Use shared hook for all async select logic with state management
  const {
    options,
    loadOptions,
    handleMenuScrollToBottom,
    scrollContainerRef,
    fetchSelectedValues,
    isLoadingMore,
  } = useAsyncSelect({
    apiEndpoint,
    searchField,
    valueField,
    labelField,
    pageSize,
  });

  // State for selected options
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Load options when input changes
   */
  useEffect(() => {
    setIsLoading(true);
    loadOptions(inputValue, () => {
      setIsLoading(false);
    });
  }, [inputValue, loadOptions]);

  // Load selected values when value prop changes
  useEffect(() => {
    if (value && value.length > 0) {
      fetchSelectedValues(value).then(options => {
        if (options.length > 0) {
          setSelectedOptions(options);
        }
      });
    } else {
      setSelectedOptions([]);
    }
  }, [value, fetchSelectedValues]);

  const handleChange = (newValue: MultiValue<SelectOption>) => {
    const options = newValue as SelectOption[];
    console.log('[AsyncMultiSelectField] 📝 Selection changed:', {
      count: options.length,
    });
    setSelectedOptions(options);
    onChange?.(options.map(opt => opt.value));
  };

  // Custom MenuList component to detect scroll to bottom and show loading footer
  const MenuList = (props: MenuListProps<SelectOption>) => {
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      const bottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

      if (bottom && !isLoadingMore) {
        handleMenuScrollToBottom();
      }
    };

    const combinedRef = (element: HTMLDivElement | null) => {
      scrollContainerRef(element);
      if (props.innerRef) {
        if (typeof props.innerRef === 'function') {
          props.innerRef(element);
        } else {
          (
            props.innerRef as React.MutableRefObject<HTMLDivElement | null>
          ).current = element;
        }
      }
    };

    return (
      <>
        <components.MenuList
          {...props}
          innerRef={combinedRef}
          innerProps={{
            ...props.innerProps,
            onScroll: handleScroll,
          }}>
          {props.children}
        </components.MenuList>
        {isLoadingMore && (
          <div
            style={{
              padding: '8px 12px',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '14px',
              borderTop: '1px solid #e5e7eb',
            }}>
            ⏳ Loading more...
          </div>
        )}
      </>
    );
  };

  return (
    <div style={styles.floatingFieldContainer}>
      <Select
        inputId={id}
        value={selectedOptions}
        onChange={handleChange}
        onInputChange={(newValue, actionMeta) => {
          if (actionMeta.action === 'input-change') {
            setInputValue(newValue);
          }
        }}
        options={options}
        components={{ MenuList }}
        isMulti
        isLoading={isLoading}
        isDisabled={disabled}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder="Search..."
        menuPortalTarget={document.body}
        filterOption={() => true}
        styles={{
          control: base => ({ ...base, ...getFloatingInputStyle(false, id) }),
          menuPortal: base => ({ ...base, zIndex: 10001 }),
        }}
      />
      <label htmlFor={id} style={getFloatingLabelStyle(id, hasValue)}>
        {label}
        {required && <span style={{ color: 'red' }}>*</span>}
      </label>
    </div>
  );
};

export default React.memo(AsyncMultiSelectField);
